import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  IconButton, Tooltip, Popover, List, ListItem, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Divider, CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FlagIcon from "@material-ui/icons/Flag";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SvgIcon from "@material-ui/core/SvgIcon";

// Bell icon (Feather Icons style) — indica recordatorio/pendiente
const BellIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24" style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", ...props.style }}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <line x1="12" y1="2" x2="12" y2="4" />
  </SvgIcon>
);
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import {
  REASON_LABELS,
  SNOOZE_PRESETS,
  calcDueDate,
  getPending,
  createPending,
  updatePending,
  resolvePending,
} from "../../services/ticketPendingService";

const useStyles = makeStyles((theme) => ({
  // Botón según estado
  btnNone: { color: theme.palette.action.active },
  btnPending: { color: "#f59e0b" },
  btnOverdue: { color: "#ef4444" },

  popoverList: { minWidth: 200, paddingTop: 4, paddingBottom: 4 },
  presetItem: { paddingTop: 6, paddingBottom: 6 },
  presetDivider: { margin: "4px 0" },

  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingTop: 8,
    minWidth: 340,
  },

  statusBanner: {
    padding: "8px 12px",
    borderRadius: 6,
    marginBottom: 4,
    fontSize: "0.85rem",
  },
  bannerPending: { backgroundColor: "#fef3c7", color: "#92400e" },
  bannerOverdue: { backgroundColor: "#fee2e2", color: "#991b1b" },
}));

const TicketPendingButton = ({ ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(true);

  // Popover quick picker
  const [anchorEl, setAnchorEl] = useState(null);

  // Modal completo
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [saving, setSaving] = useState(false);

  // Campos del modal
  const [reasonCode, setReasonCode] = useState("waiting_supplier");
  const [note, setNote] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showCustomDate, setShowCustomDate] = useState(false);

  // Cargar pending actual al montar o cuando cambia el ticket
  const loadPending = useCallback(async () => {
    if (!ticket?.id) return;
    setLoadingPending(true);
    try {
      const data = await getPending(ticket.id);
      setPending(data);
    } catch (err) {
      // Error real (no 404)
      toastError(err);
    } finally {
      setLoadingPending(false);
    }
  }, [ticket?.id]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // Determinar color/icono del botón según estado del pending
  const getBtnClass = () => {
    if (!pending || pending.status === "resolved") return classes.btnNone;
    if (pending.status === "overdue") return classes.btnOverdue;
    return classes.btnPending;
  };

  const getBtnTooltip = () => {
    if (!pending || pending.status === "resolved") return "Postergar chat";
    if (pending.status === "overdue") return "⚠️ Pendiente vencido — clic para gestionar";
    return "Chat pendiente — clic para gestionar";
  };

  // Abrir quick picker
  const handleBtnClick = (e) => {
    if (pending && pending.status !== "resolved") {
      // Ya tiene pendiente → abrir modal de gestión directamente
      openModalEdit();
    } else {
      // Sin pendiente → abrir quick picker
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClosePopover = () => setAnchorEl(null);

  // Seleccionar preset del quick picker
  const handlePresetClick = (preset) => {
    handleClosePopover();
    openModalCreate(preset);
  };

  // Abrir modal en modo creación
  const openModalCreate = (preset = null) => {
    setModalMode("create");
    setReasonCode("waiting_supplier");
    setNote("");
    setSelectedPreset(preset);
    setShowCustomDate(preset?.special === "custom");

    // Pre-rellenar fecha custom con mañana 9:00 como default
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    // Ajustar a formato datetime-local (sin Z, en hora local)
    const localIso = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);
    setCustomDate(localIso);

    setModalOpen(true);
  };

  // Abrir modal en modo edición
  const openModalEdit = () => {
    if (!pending) return;
    setModalMode("edit");
    setReasonCode(pending.reasonCode || "waiting_supplier");
    setNote(pending.note || "");
    setSelectedPreset(null);
    setShowCustomDate(false);
    setCustomDate("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setShowCustomDate(false);
  };

  // Guardar (crear o actualizar)
  const handleSave = async () => {
    setSaving(true);
    try {
      let dueDate = null;

      if (showCustomDate && customDate) {
        dueDate = new Date(customDate).toISOString();
      } else if (selectedPreset) {
        dueDate = calcDueDate(selectedPreset);
      }

      const payload = {
        reasonCode,
        note: note || null,
        dueDate,
        snoozePreset: selectedPreset?.key || (showCustomDate ? "custom" : "no_due_date"),
      };

      if (modalMode === "create") {
        const created = await createPending(ticket.id, payload);
        setPending(created);
        toast.success("Chat marcado como pendiente");
      } else {
        const updated = await updatePending(pending.id, payload);
        setPending(updated);
        toast.success("Pendiente actualizado");
      }

      handleCloseModal();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Resolver pendiente
  const handleResolve = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await resolvePending(pending.id);
      setPending(null);
      toast.success("Pendiente resuelto");
      handleCloseModal();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loadingPending) return null;

  const hasPending = pending && pending.status !== "resolved";

  return (
    <>
      {/* BOTÓN EN HEADER */}
      <Tooltip title={getBtnTooltip()} placement="bottom">
        <IconButton
          size="small"
          className={getBtnClass()}
          onClick={handleBtnClick}
          style={{ marginRight: 4 }}
        >
          {hasPending && pending.status === "overdue"
            ? <FlagIcon fontSize="small" />
            : <BellIcon fontSize="small" />
          }
        </IconButton>
      </Tooltip>

      {/* QUICK PICKER POPOVER */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <List className={classes.popoverList} dense>
          <ListItem style={{ paddingBottom: 2 }}>
            <ListItemText
              primary={
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  POSTERGAR CHAT
                </Typography>
              }
            />
          </ListItem>
          <Divider className={classes.presetDivider} />
          {SNOOZE_PRESETS.map((preset) => (
            <ListItem
              key={preset.key}
              button
              className={classes.presetItem}
              onClick={() => handlePresetClick(preset)}
            >
              <ListItemText primary={preset.label} />
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* MODAL COMPLETO */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "create" ? "Postergar chat" : "Gestionar pendiente"}
        </DialogTitle>
        <DialogContent className={classes.modalContent}>

          {/* Banner de estado si hay pendiente activo */}
          {modalMode === "edit" && pending && (
            <div className={`${classes.statusBanner} ${
              pending.status === "overdue" ? classes.bannerOverdue : classes.bannerPending
            }`}>
              {pending.status === "overdue"
                ? `⚠️ Vencido — venció el ${new Date(pending.dueDate).toLocaleString("es-AR")}`
                : pending.dueDate
                  ? `⏰ Pendiente hasta ${new Date(pending.dueDate).toLocaleString("es-AR")}`
                  : "📌 Pendiente sin fecha definida"
              }
            </div>
          )}

          {/* Motivo (obligatorio) */}
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Motivo *</InputLabel>
            <Select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              label="Motivo *"
            >
              {Object.entries(REASON_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Nota opcional */}
          <TextField
            label="Nota (opcional)"
            multiline
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ej: Esperando precio del proveedor López por caño 3/4"
            inputProps={{ maxLength: 1000 }}
          />

          {/* Selector de tiempo */}
          {modalMode === "create" && (
            <>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>¿Cuándo recordar?</InputLabel>
                <Select
                  value={selectedPreset?.key || "no_due_date"}
                  onChange={(e) => {
                    const preset = SNOOZE_PRESETS.find(p => p.key === e.target.value);
                    setSelectedPreset(preset);
                    setShowCustomDate(preset?.special === "custom");
                  }}
                  label="¿Cuándo recordar?"
                >
                  {SNOOZE_PRESETS.map((preset) => (
                    <MenuItem key={preset.key} value={preset.key}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showCustomDate && (
                <TextField
                  label="Fecha y hora"
                  type="datetime-local"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              )}
            </>
          )}

          {/* En modo edit: opción de reprogramar */}
          {modalMode === "edit" && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowCustomDate(!showCustomDate)}
              style={{ alignSelf: "flex-start" }}
            >
              {showCustomDate ? "Cancelar reprogramar" : "Reprogramar fecha"}
            </Button>
          )}

          {modalMode === "edit" && showCustomDate && (
            <TextField
              label="Nueva fecha y hora"
              type="datetime-local"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              size="small"
            />
          )}

        </DialogContent>
        <DialogActions style={{ justifyContent: "space-between", padding: "8px 16px 16px" }}>
          {/* Resolver (solo en modo edit) */}
          {modalMode === "edit" && (
            <Button
              onClick={handleResolve}
              disabled={saving}
              style={{ color: "#22c55e" }}
              startIcon={<CheckCircleIcon />}
            >
              Marcar resuelto
            </Button>
          )}

          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <Button onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving || !reasonCode}
            >
              {saving
                ? <CircularProgress size={18} color="inherit" />
                : modalMode === "create" ? "Postergar" : "Guardar cambios"
              }
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketPendingButton;
